import { restoreScriptBackup } from "./backup";
import { validateStunServers } from "./collaboration-protocol";
import type { AppSettings, PlaybackState, ScriptDocument } from "./types";

export const COMPLETE_BACKUP_FORMAT = "linebyline-complete-backup";
export const COMPLETE_BACKUP_VERSION = 1;

export interface CompleteBackup {
  format: typeof COMPLETE_BACKUP_FORMAT;
  version: typeof COMPLETE_BACKUP_VERSION;
  exportedAt: string;
  data: {
    settings: AppSettings;
    scripts: ScriptDocument[];
    playback: PlaybackState[];
  };
}

export function exportCompleteBackup(settings: AppSettings, scripts: ScriptDocument[], playback: PlaybackState[]): string {
  const backup: CompleteBackup = {
    format: COMPLETE_BACKUP_FORMAT,
    version: COMPLETE_BACKUP_VERSION,
    exportedAt: new Date().toISOString(),
    data: { settings, scripts, playback },
  };
  return JSON.stringify(backup, null, 2);
}

export function restoreCompleteBackup(source: string, defaults: AppSettings): CompleteBackup["data"] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(source);
  } catch {
    throw new Error("This backup is not valid JSON.");
  }
  const root = requireRecord(parsed, "The backup does not contain complete backup data.");
  if (root.format !== COMPLETE_BACKUP_FORMAT) throw new Error("This is not a LineByLine complete backup.");
  if (root.version !== COMPLETE_BACKUP_VERSION) throw new Error(`Backup version ${String(root.version)} is not supported. This app supports version ${COMPLETE_BACKUP_VERSION}.`);
  if (!validDate(root.exportedAt)) throw new Error("The backup has no valid export date.");

  const data = requireRecord(root.data, "The backup has no data section.");
  const settings = readSettings(data.settings, defaults);
  const scriptValues = requireArray(data.scripts, "The backup has no scripts list.");
  const scripts = scriptValues.map((script) => restoreScriptBackup(JSON.stringify(script), settings));
  if (new Set(scripts.map((script) => script.id)).size !== scripts.length) throw new Error("The backup contains duplicate script IDs.");
  const scriptIds = new Set(scripts.map((script) => script.id));
  const playback = requireArray(data.playback, "The backup has no playback list.").map(readPlayback).filter((state) => scriptIds.has(state.scriptId));
  return { settings, scripts, playback };
}

function readSettings(value: unknown, defaults: AppSettings): AppSettings {
  const settings = requireRecord(value, "The backup has no app settings.");
  const theme = settings.theme === "light" || settings.theme === "dark" || settings.theme === "system" ? settings.theme : defaults.theme;
  const stunServers = validateStunServers(stringArrayOr(settings.stunServers, defaults.stunServers));
  return {
    theme,
    defaultLanguage: stringOr(settings.defaultLanguage, defaults.defaultLanguage),
    defaultVoiceId: typeof settings.defaultVoiceId === "string" ? settings.defaultVoiceId : undefined,
    defaultPlaybackSpeed: numberOr(settings.defaultPlaybackSpeed, defaults.defaultPlaybackSpeed),
    recognitionThreshold: numberOr(settings.recognitionThreshold, defaults.recognitionThreshold),
    ignoredWords: stringArrayOr(settings.ignoredWords, defaults.ignoredWords),
    largerText: booleanOr(settings.largerText, defaults.largerText),
    openDyslexic: booleanOr(settings.openDyslexic, defaults.openDyslexic),
    highContrast: booleanOr(settings.highContrast, defaults.highContrast),
    reduceMotion: booleanOr(settings.reduceMotion, defaults.reduceMotion),
    betaSmartMemorisation: booleanOr(settings.betaSmartMemorisation, defaults.betaSmartMemorisation),
    scriptFolders: stringArrayOr(settings.scriptFolders, defaults.scriptFolders),
    stunServers,
    collaborationName: typeof settings.collaborationName === "string" ? settings.collaborationName.trim() : defaults.collaborationName,
    enableCollaborativeRehearsal: booleanOr(settings.enableCollaborativeRehearsal, defaults.enableCollaborativeRehearsal),
    characterVoiceDefaults: stringRecordOr(settings.characterVoiceDefaults, defaults.characterVoiceDefaults),
  };
}

function readPlayback(value: unknown, index: number): PlaybackState {
  const state = requireRecord(value, `Playback entry ${index + 1} is invalid.`);
  if (typeof state.scriptId !== "string" || !state.scriptId) throw new Error(`Playback entry ${index + 1} has no script ID.`);
  if (typeof state.entryIndex !== "number" || !Number.isFinite(state.entryIndex)) throw new Error(`Playback entry ${index + 1} has no line position.`);
  if (!validDate(state.updatedAt)) throw new Error(`Playback entry ${index + 1} has no valid date.`);
  return { scriptId: state.scriptId, entryIndex: state.entryIndex, updatedAt: state.updatedAt as string };
}

function requireRecord(value: unknown, message: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error(message);
  return value as Record<string, unknown>;
}

function requireArray(value: unknown, message: string): unknown[] {
  if (!Array.isArray(value)) throw new Error(message);
  return value;
}

function validDate(value: unknown): boolean {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}

function stringOr(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() ? value : fallback;
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function booleanOr(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function stringArrayOr(value: unknown, fallback: string[]): string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string") ? value : [...fallback];
}

function stringRecordOr(value: unknown, fallback: Record<string, string>): Record<string, string> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return { ...fallback };
  const entries = Object.entries(value).filter((entry): entry is [string, string] => Boolean(entry[0].trim()) && typeof entry[1] === "string" && Boolean(entry[1]));
  return Object.fromEntries(entries.map(([name, voice]) => [name.trim().toLowerCase(), voice]));
}
